import clsx from "clsx";
import {
  Fragment,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { EmptyState } from "../components/EmptyState.tsx";
import { DataTable } from "../components/DataTable/index.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { formatSeasonRecord } from "../util/seasonRecord.ts";
import { createMobileCrossTabHref } from "../util/mobileCrossTabLink.ts";
import { toWorker } from "../util/toWorker.ts";
import { getCols } from "../util/getCols.ts";
import type {
  DraftLotteryResultArray,
  View,
  DraftType,
  DraftPickWithoutKey,
} from "../../../../common/types.ts";
import { getDraftLotteryProbs } from "../../../../common/draftLottery.ts";
import { range } from "../../../../common/utils.ts";
import {
  COLA_OPT_OUT_PENALTY,
  NO_LOTTERY_DRAFT_TYPES,
} from "../../../../common/constants.ts";
import { wrappedTeamLogoAndName } from "../components/TeamLogoAndName.tsx";
import { PlayPauseNext } from "../components/PlayPauseNext.tsx";
import { DraftAbbrev } from "../components/DraftAbbrev.tsx";
import { MobileTooltip } from "../components/MobileTooltip.tsx";
import {
  MobileArchiveSeasonNav,
  MobileContentHero,
  MobileExpertDisclosure,
  MobileStatRow,
} from "../mobile/mobilePrimitives.tsx";
import i18n from "../util/i18n.ts";
import { ordinalLocalized } from "../util/ordinalLocalized.ts";
import MobileListToggle, {
  useMobileListVisibleCount,
} from "../components/MobileListToggle.tsx";
import {
  getDraftLotteryDecisionContext,
  getDraftLotteryLuckDelta,
  getDraftLotteryStage,
} from "./draftStageTemplate.ts";

type MyDraftType = DraftType | "dummy";
export const getDraftTypeDescription = (
  type: MyDraftType,
  showPrefix: boolean = false,
) => {
  const types: Record<MyDraftType, ReactNode> = {
    nba2019: i18n.t("draftLottery.typeNba2019", {
      ns: "ui",
      defaultValue:
        "Weighted lottery for the top 4 picks, like the NBA since 2019",
    }),
    nba1994: i18n.t("draftLottery.typeNba1994", {
      ns: "ui",
      defaultValue:
        "Weighted lottery for the top 3 picks, like the NBA from 1994-2018",
    }),
    nba1990: i18n.t("draftLottery.typeNba1990", {
      ns: "ui",
      defaultValue:
        "Weighted lottery for the top 3 picks, like the NBA from 1990-1993",
    }),
    nhl2017: i18n.t("draftLottery.typeNhl2017", {
      ns: "ui",
      defaultValue:
        "Weighted lottery for the top 3 picks, like the NHL from 2017-2020",
    }),
    nhl2021: i18n.t("draftLottery.typeNhl2021", {
      ns: "ui",
      defaultValue:
        "Weighted lottery for the top 2 picks, like the NHL since 2021",
    }),
    mlb2022: i18n.t("draftLottery.typeMlb2022", {
      ns: "ui",
      defaultValue:
        "Weighted lottery for the top 6 picks, like the MLB since 2022",
    }),
    randomLotteryFirst3: i18n.t("draftLottery.typeRandomLotteryFirst3", {
      ns: "ui",
      defaultValue:
        "Random lottery for the top 3 picks, like the NBA from 1987-1989",
    }),
    randomLottery: i18n.t("draftLottery.typeRandomLottery", {
      ns: "ui",
      defaultValue:
        "Non-playoff teams draft in random order, like the NBA from 1985-1986",
    }),
    coinFlip: i18n.t("draftLottery.typeCoinFlip", {
      ns: "ui",
      defaultValue:
        "Coin flip to determine the top 2 picks, like the NBA from 1966-1984",
    }),
    noLottery: i18n.t("draftLottery.typeNoLottery", {
      ns: "ui",
      defaultValue:
        "No lottery, teams draft in order of their record, from worst to best with non-playoff teams coming first",
    }),
    noLotteryReverse: i18n.t("draftLottery.typeNoLotteryReverse", {
      ns: "ui",
      defaultValue:
        "No lottery, teams draft in order of their record, from best to worst with playoff teams coming first",
    }),
    random: i18n.t("draftLottery.typeRandom", {
      ns: "ui",
      defaultValue: "Teams draft in random order, including playoff teams",
    }),
    freeAgents: i18n.t("draftLottery.typeFreeAgents", {
      ns: "ui",
      defaultValue:
        "There is no draft and all, rookies simply become free agents who can be signed by any team",
    }),
    custom: i18n.t("draftLottery.typeCustom", {
      ns: "ui",
      defaultValue: "Custom weighted lottery for the top N picks.",
    }),
    dummy: i18n.t("draftLottery.typeDummy", {
      ns: "ui",
      defaultValue: "From historical data",
    }),
    cola: (
      <>
        {showPrefix
          ? i18n.t("draftLottery.colaDescWithPrefix", {
              ns: "ui",
              defaultValue: "Carry-Over Lottery Allocation (COLA), a",
            })
          : i18n.t("draftLottery.colaDescNoPrefix", {
              ns: "ui",
              defaultValue: "A",
            })}{" "}
        {i18n.t("draftLottery.colaDescSuffix", {
          ns: "ui",
          defaultValue:
            "new draft lottery method that eliminates tanking while also giving the best picks to bad teams.",
        })}
      </>
    ),
  };

  return types[type];
};

type Props = View<"draftLottery">;
type State = {
  draftType: Props["draftType"];
  result: Props["result"];
  season: Props["season"];
  toReveal: number[];
  // Values are indexes of props.result, starting with the 14th pick and ending with the 1st pick
  indRevealed: number;
  revealState: "init" | "running" | "paused" | "done";
};

type Action =
  | {
      type: "init";
      props: Pick<Props, "result" | "season" | "draftType">;
    }
  | {
      type: "startClicked";
    }
  | {
      type: "start";
      draftType: DraftType;
      result: DraftLotteryResultArray;
      toReveal: number[];
      indRevealed: number;
    }
  | {
      type: "pause";
    }
  | {
      type: "resume";
    }
  | {
      type: "revealOne";
    };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "init":
      return {
        draftType: action.props.draftType,
        result: action.props.result,
        toReveal: [],
        indRevealed: -1,
        revealState: "init",
        season: action.props.season,
      };
    case "startClicked":
      return {
        ...state,
        revealState: "running",
      };
    case "start":
      return {
        ...state,
        draftType: action.draftType,
        result: action.result,
        toReveal: action.toReveal,
        indRevealed: action.indRevealed,
        revealState: "running",
      };
    case "pause":
      return {
        ...state,
        revealState: "paused",
      };
    case "resume":
      return {
        ...state,
        revealState: "running",
      };
    case "revealOne": {
      const indRevealed = state.indRevealed + 1;
      const revealState =
        indRevealed >= state.toReveal.length - 1 ? "done" : state.revealState;
      return {
        ...state,
        indRevealed,
        revealState,
      };
    }
  }
};

type DraftLotteryMobileField = {
  className?: string;
  label: ReactNode;
  value: ReactNode;
};

const DraftLotteryMobileCard = ({
  NUM_PICKS,
  i,
  pickAlreadyMade,
  season,
  t,
  usePts,
  userTid,
  indRevealed,
  toReveal,
  probs,
  spectator,
  teams,
}: Pick<Props, "teams" | "usePts" | "userTid"> & {
  NUM_PICKS: number;
  i: number;
  pickAlreadyMade: boolean;
  season: number;
  t: DraftLotteryResultArray[number];
  indRevealed: State["indRevealed"];
  toReveal: State["toReveal"];
  probs: NonNullable<ReturnType<typeof getDraftLotteryProbs>["probs"]>;
  spectator: boolean;
}) => {
  const { tid, originalTid, chances, pick, dpid } = t;
  const userTeam = tid === userTid;

  let revealedPickNumber: number | null = null;
  const pickFields = range(NUM_PICKS).flatMap(
    (j): DraftLotteryMobileField[] => {
      const prob = probs[i]![j]!;
      const pct =
        prob !== undefined ? `${(prob * 100).toFixed(1)}%` : undefined;

      let highlighted = false;

      if (pick !== undefined) {
        highlighted = pick === j + 1;
        revealedPickNumber = pick;
      } else if (NUM_PICKS - 1 - j <= indRevealed) {
        const ind = toReveal.indexOf(i);

        if (ind === NUM_PICKS - 1 - j) {
          highlighted = true;
          revealedPickNumber = j + 1;
        }
      }

      if (pct === undefined) {
        return [];
      }

      return [
        {
          className: clsx({
            "table-success": highlighted && !userTeam,
            "table-info": highlighted && userTeam,
          }),
          label: ordinalLocalized(j + 1),
          value: pct,
        },
      ];
    },
  );

  const fields: DraftLotteryMobileField[] = [
    ...(revealedPickNumber !== null
      ? [
          {
            label: "#",
            value: revealedPickNumber,
          },
        ]
      : []),
    {
      label: i18n.t("draftLottery.record", {
        ns: "ui",
        defaultValue: "Record",
      }),
      value: (
        <>
          {usePts ? `${teams[originalTid]?.seasonAttrs.pts ?? 0} pts (` : null}
          {formatSeasonRecord(
            teams[originalTid]?.seasonAttrs ?? {
              won: 0,
              lost: 0,
            },
          )}
          {usePts ? `)` : null}
        </>
      ),
    },
    {
      label: i18n.t("draftLottery.chances", {
        ns: "ui",
        defaultValue: "Chances",
      }),
      value: chances,
    },
    ...pickFields,
  ];
  const visibleFields = fields.slice(0, 6);
  const overflowFields = fields.slice(6);
  const canTrade = !(userTeam || spectator || pickAlreadyMade);

  return (
    <article
      className={clsx("datatable-mobile-card", {
        "table-info": userTeam,
      })}
      data-draft-lottery-card-kind="lottery"
      data-draft-lottery-card-overflow-field-count={overflowFields.length}
      data-draft-lottery-card-tid={tid}
      data-draft-lottery-card-visible-field-count={visibleFields.length}
      role="listitem"
    >
      <div className="datatable-mobile-card-title">
        <DraftAbbrev
          t={teams[tid]?.seasonAttrs}
          tid={tid}
          originalT={teams[originalTid]?.seasonAttrs}
          originalTid={originalTid}
          season={season}
          showLogos
        />
      </div>
      {canTrade ? (
        <div className="datatable-mobile-card-actions">
          <button
            className="btn btn-light-bordered w-100"
            onClick={async () => {
              await toWorker("actions", "tradeFor", {
                dpid,
                tid,
              });
            }}
            type="button"
          >
            {i18n.t("draftLottery.trade", { ns: "ui", defaultValue: "Trade" })}
          </button>
        </div>
      ) : null}
      <dl className="datatable-mobile-card-fields">
        {visibleFields.map((field, i) => (
          <div
            className={clsx("datatable-mobile-card-field", field.className)}
            key={i}
          >
            <dt>{field.label}</dt>
            <dd>{field.value}</dd>
          </div>
        ))}
      </dl>
      {overflowFields.length > 0 ? (
        <details
          className="datatable-mobile-card-more"
          data-draft-lottery-card-details=""
          data-native-details-host="draft-lottery-card-detail"
        >
          <summary>
            <span className="datatable-mobile-card-more-closed">
              {i18n.t("dataTable.viewAll", {
                ns: "ui",
                defaultValue: "View all",
              })}
            </span>
            <span className="datatable-mobile-card-more-open">
              {i18n.t("dataTable.collapseFields", {
                ns: "ui",
                defaultValue: "Collapse",
              })}
            </span>
          </summary>
          <dl className="datatable-mobile-card-fields">
            {overflowFields.map((field, i) => (
              <div
                className={clsx("datatable-mobile-card-field", field.className)}
                key={i}
              >
                <dt>{field.label}</dt>
                <dd>{field.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
    </article>
  );
};

const DraftLotteryMobileNonLotteryCard = ({
  dp,
  pickAlreadyMade,
  spectator,
  teams,
  usePts,
  userTid,
}: Pick<Props, "teams" | "usePts" | "userTid"> & {
  dp: DraftPickWithoutKey;
  pickAlreadyMade: boolean;
  spectator: boolean;
}) => {
  const userTeam = dp.tid === userTid;
  const season = dp.season as number;
  const t = teams[dp.tid];
  const canTrade = !(userTeam || spectator || pickAlreadyMade);

  return (
    <article
      className={clsx("datatable-mobile-card", {
        "table-info": userTeam,
      })}
      data-draft-lottery-card-kind="non-lottery"
      data-draft-lottery-card-pick={dp.pick}
      data-draft-lottery-card-round={dp.round}
      data-draft-lottery-card-tid={dp.tid}
      role="listitem"
    >
      <div className="datatable-mobile-card-title">
        <DraftAbbrev
          t={t?.seasonAttrs}
          tid={dp.tid}
          originalT={teams[dp.originalTid]?.seasonAttrs}
          originalTid={dp.originalTid}
          season={season}
          showLogos
        />
      </div>
      {canTrade ? (
        <div className="datatable-mobile-card-actions">
          <button
            className="btn btn-light-bordered w-100"
            onClick={async () => {
              await toWorker("actions", "tradeFor", {
                dpid: dp.dpid,
                tid: dp.tid,
              });
            }}
            type="button"
          >
            {i18n.t("draftLottery.trade", { ns: "ui", defaultValue: "Trade" })}
          </button>
        </div>
      ) : null}
      <dl className="datatable-mobile-card-fields">
        <div className="datatable-mobile-card-field">
          <dt>
            <MobileTooltip
              title={i18n.t("draftLottery.pickNumber", {
                ns: "ui",
                defaultValue: "Pick number",
              })}
            >
              <span>#</span>
            </MobileTooltip>
          </dt>
          <dd>{dp.pick}</dd>
        </div>
        <div className="datatable-mobile-card-field">
          <dt>
            {i18n.t("draftLottery.record", {
              ns: "ui",
              defaultValue: "Record",
            })}
          </dt>
          <dd>
            {usePts ? `${t?.seasonAttrs.pts ?? 0} pts (` : null}
            {formatSeasonRecord(
              t?.seasonAttrs ?? {
                won: 0,
                lost: 0,
              },
            )}
            {usePts ? `)` : null}
          </dd>
        </div>
      </dl>
    </article>
  );
};

const NonLotteryMobileHeader = ({
  children,
  round,
}: {
  children: ReactNode;
  round?: number;
}) => {
  return (
    <div
      className="fw-semibold text-center text-secondary"
      data-draft-lottery-round-header={round}
    >
      {children}
    </div>
  );
};

const DraftLotteryTable = (props: Props) => {
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // This is redundant with state because of https://github.com/facebook/react/issues/14010
  const numLeftToReveal = useRef(0); // Like indRevealed
  const revealState = useRef<State["revealState"]>("init");

  const timeoutID = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const [state, dispatch] = useReducer(reducer, {
    draftType: props.draftType,
    result: props.result,
    toReveal: [],
    indRevealed: -1,
    revealState: "init",
    season: props.season,
  });

  // Handle changing season, and updating state.result due to game sim
  if (
    props.season !== state.season ||
    props.draftType !== state.draftType ||
    (revealState.current === "init" && props.result !== state.result)
  ) {
    numLeftToReveal.current = 0;
    revealState.current = "init";
    dispatch({ type: "init", props });
  }

  const revealPickAuto = () => {
    if (
      !isMounted.current ||
      numLeftToReveal.current <= 0 ||
      revealState.current !== "running"
    ) {
      return;
    }

    numLeftToReveal.current -= 1;
    dispatch({ type: "revealOne" });

    if (numLeftToReveal.current >= 1) {
      timeoutID.current = setTimeout(revealPickAuto, 1000);
    }
  };

  const startLottery = async () => {
    dispatch({ type: "startClicked" });
    const draftLotteryResult = await toWorker(
      "main",
      "draftLottery",
      undefined,
    );
    if (draftLotteryResult) {
      const { draftType, result } = draftLotteryResult;

      const toReveal: number[] = [];

      for (const [i, row] of result.entries()) {
        const pick = row.pick;
        if (pick !== undefined) {
          toReveal[pick - 1] = i;
        }
        row.pick = undefined;
      }
      toReveal.reverse();

      revealState.current = "running";
      numLeftToReveal.current = toReveal.length;
      dispatch({ type: "start", draftType, result, toReveal, indRevealed: -1 });

      revealPickAuto();
    }
  };

  const handleResume = () => {
    revealState.current = "running";
    dispatch({ type: "resume" });

    revealPickAuto();
  };

  const handlePause = () => {
    clearTimeout(timeoutID.current);
    timeoutID.current = undefined;
    revealState.current = "paused";
    dispatch({ type: "pause" });
  };

  const handleShowOne = () => {
    numLeftToReveal.current -= 1;
    dispatch({ type: "revealOne" });
  };

  const {
    colaOptOutAvailable,
    colaOptOutStatus,
    currentSeason,
    dpidsAvailableToTrade,
    draftPicks,
    numToPick,
    season,
    teams,
    type,
    usePts,
    userTid,
  } = props;
  const { draftType, result } = state;
  const { tooSlow, probs } = getDraftLotteryProbs(result, draftType, numToPick);
  const NUM_PICKS = result !== undefined ? result.length : 14;

  const [showAll, setShowAll] = useState(false);
  const [mobileLotteryExpanded, setMobileLotteryExpanded] = useState(false);
  const mobileLotteryListRef = useRef<HTMLDivElement>(null);

  const showStartButton =
    type === "readyToRun" &&
    state.revealState === "init" &&
    result &&
    result.length > 0;

  let table;

  if (props.notEnoughTeams) {
    return (
      <div className="alert alert-danger d-inline-block">
        <p>
          {i18n.t("draftLottery.notEnoughTeams", {
            ns: "ui",
            defaultValue:
              "Error: Not enough teams for your selected lottery type, so there will be no draft lottery.",
          })}
        </p>
        {props.challengeWarning ? (
          <p>
            {i18n.t("draftLottery.noDraftPicksChallenge", {
              ns: "ui",
              defaultValue:
                'This is probably because you\'re using the "no draft picks" challenge mode and controlling many teams in multi team mode, so not enough teams are eligible for the draft lottery.',
            })}
          </p>
        ) : null}
      </div>
    );
  }

  const isHistoricalLottery = type === "completed" && season < currentSeason;
  const otherDraftPicksToShow = draftPicks?.filter(
    (dp) => showAll || dp.round === 1,
  );

  let seenRoundMobile = 1;
  const otherDraftPickMobileCards =
    otherDraftPicksToShow && otherDraftPicksToShow.length > 0 ? (
      <>
        {otherDraftPicksToShow.some((dp) => dp.round === 1) ? (
          <NonLotteryMobileHeader>
            {i18n.t("draftLottery.nonLotteryPicks", {
              ns: "ui",
              defaultValue: "Non-lottery picks",
            })}
          </NonLotteryMobileHeader>
        ) : null}
        {otherDraftPicksToShow.map((dp, i) => {
          const showRoundHeader = seenRoundMobile !== dp.round;
          if (showRoundHeader) {
            seenRoundMobile = dp.round;
          }

          return (
            <Fragment key={i}>
              {showRoundHeader ? (
                <NonLotteryMobileHeader round={dp.round}>
                  {i18n.t("draftLottery.ordinalRound", {
                    ns: "ui",
                    defaultValue: "{{ordinal}} round",
                    ordinal: ordinalLocalized(dp.round),
                  })}
                </NonLotteryMobileHeader>
              ) : null}
              <DraftLotteryMobileNonLotteryCard
                dp={dp}
                pickAlreadyMade={!dpidsAvailableToTrade.has(dp.dpid)}
                spectator={props.spectator || isHistoricalLottery}
                teams={teams}
                usePts={usePts}
                userTid={userTid}
              />
            </Fragment>
          );
        })}
      </>
    ) : null;
  const mobileLotteryKeySignature =
    result
      ?.map((t, i) => `${i}:${t.tid}:${t.dpid}:${t.pick ?? ""}`)
      .join("\u0001") ?? "";
  useEffect(() => {
    setMobileLotteryExpanded(false);
  }, [mobileLotteryKeySignature]);
  const {
    canCollapse: canCollapseMobileLotteryResults,
    visibleCount: mobileLotteryVisibleCount,
  } = useMobileListVisibleCount({
    expanded: mobileLotteryExpanded,
    itemCount: result?.length ?? 0,
    itemSignature: mobileLotteryKeySignature,
    listRef: mobileLotteryListRef,
  });
  const mobileDraftLotteryStage = getDraftLotteryStage({
    hasResult: !!result && result.length > 0,
    revealState: state.revealState,
    type,
  });
  const mobileDraftLotteryDecision = getDraftLotteryDecisionContext({
    probs: probs ?? undefined,
    result,
    stage: mobileDraftLotteryStage,
    userTid,
  });
  const mobileDraftLotteryLuck = getDraftLotteryLuckDelta(
    mobileDraftLotteryDecision,
  );
  const mobileDraftLotteryTitle = isHistoricalLottery
    ? i18n.t("draftLottery.p2s03HistoryTitle", {
        ns: "ui",
        season,
        defaultValue: "{{season}} lottery archive",
      })
    : mobileDraftLotteryStage === "completed"
      ? i18n.t("draftLottery.p2v2TitleCompleted", {
          ns: "ui",
          defaultValue: "Lottery result locked",
        })
      : mobileDraftLotteryStage === "running"
        ? i18n.t("draftLottery.p2v2TitleRunning", {
            ns: "ui",
            defaultValue: "Lottery reveal in progress",
          })
        : mobileDraftLotteryStage === "projected"
          ? i18n.t("draftLottery.p2v2TitleProjected", {
              ns: "ui",
              defaultValue: "Projected lottery range",
            })
          : i18n.t("draftLottery.p2v2TitleReady", {
              ns: "ui",
              defaultValue: "Lottery ready to run",
            });
  const mobileDraftLotteryConclusion = isHistoricalLottery
    ? mobileDraftLotteryDecision.finalPick !== undefined
      ? i18n.t("draftLottery.p2s03HistoryConclusion", {
          ns: "ui",
          expected: mobileDraftLotteryDecision.expectedPick,
          pick: mobileDraftLotteryDecision.finalPick,
          season,
          defaultValue:
            "{{season}} result: your pick landed #{{pick}} against an expected slot of #{{expected}}.",
        })
      : i18n.t("draftLottery.p2s03HistoryNoUserPick", {
          ns: "ui",
          season,
          defaultValue:
            "{{season}} result is preserved here; your team did not own a highlighted lottery pick.",
        })
    : !mobileDraftLotteryDecision.hasUserPick
      ? i18n.t("draftLottery.p2v2NoUserPick", {
          ns: "ui",
          defaultValue:
            "Your team doesn't have a lottery pick right now.",
        })
      : mobileDraftLotteryDecision.finalPick !== undefined
        ? i18n.t("draftLottery.p2v2FinalConclusion", {
            ns: "ui",
            pick: mobileDraftLotteryDecision.finalPick,
            defaultValue: "Your pick landed at #{{pick}}.",
          })
        : mobileDraftLotteryDecision.bestPick !== undefined &&
            mobileDraftLotteryDecision.worstPick !== undefined
          ? i18n.t("draftLottery.p2v2RangeConclusion", {
              ns: "ui",
              best: mobileDraftLotteryDecision.bestPick,
              expected: mobileDraftLotteryDecision.expectedPick,
              topPct: mobileDraftLotteryDecision.topPickProbability,
              worst: mobileDraftLotteryDecision.worstPick,
              defaultValue:
                "Your pick is tracking between #{{best}} and #{{worst}}, with an expected pick around #{{expected}} and a {{topPct}}% shot at #1.",
            })
          : i18n.t("draftLottery.p2v2OrderConclusion", {
              ns: "ui",
              defaultValue:
                "Review the current order before planning draft-night trades.",
            });
  // G2d-1b fix: the "Asset impact" row already carries its label, so the values
  // drop the repeated "Asset impact:" prefix and the ledger/board bookkeeping
  // voice in favor of plain GM language (主窗口验收退回项 2).
  const mobileDraftLotteryAssetImpact = isHistoricalLottery
    ? i18n.t("draftLottery.p2s03HistoryAssetImpact", {
        ns: "ui",
        season,
        defaultValue:
          "Compare this result with the {{season}} draft class to see what the pick turned into.",
      })
    : mobileDraftLotteryDecision.finalPick !== undefined
      ? i18n.t("draftLottery.p1s08AssetImpactFinal", {
          ns: "ui",
          pick: mobileDraftLotteryDecision.finalPick,
          defaultValue:
            "You hold pick #{{pick}}. Decide whether to keep it before taking trade calls.",
        })
      : mobileDraftLotteryDecision.bestPick !== undefined &&
          mobileDraftLotteryDecision.worstPick !== undefined
        ? i18n.t("draftLottery.p1s08AssetImpactRange", {
            ns: "ui",
            best: mobileDraftLotteryDecision.bestPick,
            worst: mobileDraftLotteryDecision.worstPick,
            defaultValue:
              "Your pick can land anywhere from #{{best}} to #{{worst}}. Hold off on pick trades until the lottery settles.",
          })
        : i18n.t("draftLottery.p1s08AssetImpactNone", {
            ns: "ui",
            defaultValue:
              "You don't hold a lottery pick this year. If you want one, ask what other teams would take for theirs.",
          });
  const mobileDraftLotteryLuckText =
    mobileDraftLotteryLuck === undefined
      ? i18n.t("draftLottery.p2s03LuckUnavailable", {
          ns: "ui",
          defaultValue: "Luck delta unavailable",
        })
      : mobileDraftLotteryLuck.tone === "better"
        ? i18n.t("draftLottery.p2s03LuckBetter", {
            ns: "ui",
            delta: mobileDraftLotteryLuck.magnitude,
            defaultValue: "{{delta}} picks better than expected",
          })
        : mobileDraftLotteryLuck.tone === "worse"
          ? i18n.t("draftLottery.p2s03LuckWorse", {
              ns: "ui",
              delta: mobileDraftLotteryLuck.magnitude,
              defaultValue: "{{delta}} picks worse than expected",
            })
          : i18n.t("draftLottery.p2s03LuckNeutral", {
              ns: "ui",
              defaultValue: "Matched expected slot",
            });
  const p2DraftLotteryStageContext = isHistoricalLottery
    ? i18n.t("draftLottery.p2dStageHistoricalResult", {
        ns: "ui",
        season,
        defaultValue: "{{season}} is a historical lottery result.",
      })
    : mobileDraftLotteryStage === "completed"
      ? i18n.t("draftLottery.p2dStageCompleted", {
          ns: "ui",
          defaultValue: "The lottery is over — go see the full draft order.",
        })
      : mobileDraftLotteryStage === "running"
        ? i18n.t("draftLottery.p2dStageRunning", {
            ns: "ui",
            defaultValue:
              "Lottery reveal is in progress; continue the reveal before draft planning.",
          })
        : mobileDraftLotteryStage === "projected"
          ? i18n.t("draftLottery.p2dStageProjected", {
              ns: "ui",
              defaultValue:
                "The lottery hasn't started yet — use these projected odds to plan ahead.",
            })
          : i18n.t("draftLottery.p2dStageNotStarted", {
              ns: "ui",
              defaultValue:
                "Lottery has not started; review odds before entering the draft.",
            });
  // Checking both is redundant, but TypeScript wants it
  if (result && probs) {
    const mobileResultToShow = mobileLotteryExpanded
      ? result
      : result.slice(0, mobileLotteryVisibleCount);
    const canCollapseMobileLottery =
      canCollapseMobileLotteryResults || otherDraftPickMobileCards !== null;

    table = (
      <>
        <div
          className="datatable-mobile-list d-lg-none mb-3"
          ref={mobileLotteryListRef}
          role="list"
        >
          {mobileResultToShow.map((t, i) => (
            <DraftLotteryMobileCard
              key={i}
              NUM_PICKS={NUM_PICKS}
              i={i}
              pickAlreadyMade={!dpidsAvailableToTrade.has(t.dpid)}
              season={season}
              t={t}
              userTid={userTid}
              indRevealed={state.indRevealed}
              toReveal={state.toReveal}
              probs={probs}
              spectator={props.spectator || isHistoricalLottery}
              teams={teams}
              usePts={usePts}
            />
          ))}
          {mobileLotteryExpanded ? otherDraftPickMobileCards : null}
        </div>
        {canCollapseMobileLottery ? (
          <MobileListToggle
            expanded={mobileLotteryExpanded}
            onClick={() => {
              setMobileLotteryExpanded((prev) => !prev);
            }}
          />
        ) : null}
      </>
    );
  } else if (otherDraftPickMobileCards) {
    table = (
      <div className="datatable-mobile-list d-lg-none mb-3" role="list">
        {otherDraftPickMobileCards}
      </div>
    );
  } else {
    table = (
      <div>
        <EmptyState
          action={
            <a
              className="btn btn-outline-secondary"
              href={helpers.leagueUrl(["draft_history", season])}
            >
              {i18n.t("draftLottery.p2s03OpenDraftHistory", {
                ns: "ui",
                defaultValue: "Open draft history",
              })}
            </a>
          }
          reason="empty"
          title={i18n.t("draftLottery.p2s03NoResultsTitle", {
            ns: "ui",
            season,
            defaultValue: "No lottery result for {{season}}",
          })}
        >
          {i18n.t("draftLottery.noDraftLotteryResults", {
            ns: "ui",
            defaultValue: "No draft lottery results for {{season}}.",
            season,
          })}
        </EmptyState>
      </div>
    );
  }

  const desktopLotteryTable =
    result && probs ? (
      <div className="d-none d-lg-block" data-draft-lottery-desktop-table="">
        <DataTable
          className="mb-0"
          cols={getCols(["#", "Team", "Record", "Chances"])}
          defaultSort="disableSort"
          hideAllControls
          name="DraftLotteryDesktop"
          nonfluid
          pagination={false}
          rows={result.map((t, i) => ({
            key: t.dpid,
            data: [
              t.pick ?? i + 1,
              <DraftAbbrev
                key={`team-${t.dpid}`}
                t={teams[t.tid]?.seasonAttrs}
                tid={t.tid}
                originalT={teams[t.originalTid]?.seasonAttrs}
                originalTid={t.originalTid}
                season={season}
                showLogos
              />,
              formatSeasonRecord(
                teams[t.originalTid]?.seasonAttrs ?? {
                  won: 0,
                  lost: 0,
                },
              ),
              t.chances,
            ],
          }))}
        />
      </div>
    ) : null;

  return (
    <>
      <p>
        {draftType !== undefined ? (
          <>
            <b>
              {i18n.t("draftLottery.draftLotteryType", {
                ns: "ui",
                defaultValue: "Draft lottery type:",
              })}
            </b>{" "}
            {getDraftTypeDescription(draftType, true)}
          </>
        ) : null}
      </p>
      {/* G2d-1b: v5Content migration. The core-task / archive-readonly template
          wrapper and the decision-summary "lottery stage" meta card
          (conclusion/consequence/deadline narration, 法则一) are gone. The
          first screen is the lottery content itself inside one MobileContentHero:
          the stage line plus the odds / expected pick / asset-impact probability
          cards as MobileStatRow rows, with the full per-team board folded into
          the expert disclosure below. */}
      <MobileContentHero
        action={
          <a
            className={
              isHistoricalLottery
                ? "btn btn-outline-secondary"
                : "btn btn-primary"
            }
            href={
              isHistoricalLottery
                ? helpers.leagueUrl(["draft_history", season])
                : mobileDraftLotteryStage === "completed"
                  ? helpers.leagueUrl(["draft"])
                  : "#draft-lottery-detail"
            }
          >
            {isHistoricalLottery
              ? i18n.t("draftLottery.p2s06ViewDraftOrder", {
                  ns: "ui",
                  defaultValue: "View draft order",
                })
              : mobileDraftLotteryStage === "completed"
                ? i18n.t("draftLottery.p2dEnterDraft", {
                    ns: "ui",
                    defaultValue: "Enter draft",
                  })
                : i18n.t("draftLottery.p2dReviewLotteryBoard", {
                    ns: "ui",
                    defaultValue: "See the odds",
                  })}
          </a>
        }
        className="mb-3"
        data-mobile-draft-lottery-stage={mobileDraftLotteryStage}
        explainer={mobileDraftLotteryConclusion}
        title={mobileDraftLotteryTitle}
      >
        <div
          className="small text-body-secondary mb-2"
        >
          {p2DraftLotteryStageContext}
        </div>
        <div
          className="d-grid gap-2"
        >
          {isHistoricalLottery ? (
            <MobileStatRow
              detail={
                <a
                  href={helpers.leagueUrl(["draft_history", season])}
                >
                  {i18n.t("draftLottery.p2s07DraftLink", {
                    ns: "ui",
                    defaultValue: "Open final draft class",
                  })}
                </a>
              }
              label={i18n.t("draftLottery.p2s07ResultReview", {
                ns: "ui",
                defaultValue: "Lottery result review",
              })}
              value={mobileDraftLotteryLuckText}
            />
          ) : null}
          <MobileStatRow
            label={i18n.t("draftLottery.p2v2StageLabel", {
              ns: "ui",
              defaultValue: "Stage",
            })}
            value={i18n.t(
              `draftLottery.mobileStage_${mobileDraftLotteryStage}`,
              {
                ns: "ui",
                defaultValue: mobileDraftLotteryStage,
              },
            )}
          />
          {mobileDraftLotteryDecision.hasUserPick ? (
            <MobileStatRow
              highlighted
              // G2d-1b fix: once the pick is final the value is a slot, not a
              // probability, so the label switches with it (主窗口验收退回项 1).
              label={
                mobileDraftLotteryDecision.finalPick !== undefined
                  ? i18n.t("draftLottery.p2v2UserPickLabel", {
                      ns: "ui",
                      defaultValue: "Your pick",
                    })
                  : i18n.t("draftLottery.p2v2UserOdds", {
                      ns: "ui",
                      defaultValue: "Your odds",
                    })
              }
              value={
                mobileDraftLotteryDecision.finalPick !== undefined
                  ? i18n.t("draftLottery.p2v2FinalPickValue", {
                      ns: "ui",
                      pick: mobileDraftLotteryDecision.finalPick,
                      defaultValue: "#{{pick}}",
                    })
                  : i18n.t("draftLottery.p2v2UserOddsValue", {
                      ns: "ui",
                      chances: mobileDraftLotteryDecision.userChances,
                      share: mobileDraftLotteryDecision.userChanceShare,
                      defaultValue: "{{chances}} chances, {{share}}%",
                    })
              }
            />
          ) : null}
          {isHistoricalLottery ? (
            <MobileStatRow
              detail={i18n.t("draftLottery.p2s03ProbabilityFinalValue", {
                ns: "ui",
                expected: mobileDraftLotteryDecision.expectedPick ?? "-",
                final: mobileDraftLotteryDecision.finalPick ?? "-",
                luck: mobileDraftLotteryLuckText,
                defaultValue:
                  "Expected #{{expected}} -> final #{{final}}. {{luck}}.",
              })}
              label={i18n.t("draftLottery.p2s03ProbabilityFinalComparison", {
                ns: "ui",
                defaultValue: "Probability vs final result",
              })}
              value={`#${mobileDraftLotteryDecision.expectedPick ?? "-"} → #${
                mobileDraftLotteryDecision.finalPick ?? "-"
              }`}
            />
          ) : null}
          <MobileStatRow
            detail={mobileDraftLotteryAssetImpact}
            label={i18n.t("draftLottery.p1s08AssetImpact", {
              ns: "ui",
              defaultValue: "Asset impact",
            })}
            value=""
          />
        </div>
      </MobileContentHero>
      {showStartButton && colaOptOutAvailable ? (
        <div className="mb-3">
          <button
            className="btn btn-danger"
            onClick={async () => {
              await toWorker("main", "toggleColaOptOut", undefined);
            }}
          >
            {colaOptOutStatus
              ? i18n.t("draftLottery.optInToLottery", {
                  ns: "ui",
                  defaultValue: "Opt in to lottery",
                })
              : i18n.t("draftLottery.optOutOfLottery", {
                  ns: "ui",
                  defaultValue: "Opt out of lottery",
                })}
          </button>
          <p className="small text-body-secondary mt-2 mb-0" data-cola-opt-out-penalty="">
            {i18n.t("draftLottery.optOutHelp3", {
              ns: "ui",
              defaultValue:
                "There is a penalty of {{penalty}} lottery chances for opting out of the lottery.",
              penalty: COLA_OPT_OUT_PENALTY,
            })}
          </p>
        </div>
      ) : null}
      <div className="mb-3">
        {showStartButton ? (
          <button
            className="btn btn-large btn-success"
            onClick={() => startLottery()}
          >
            {i18n.t("draftLottery.startLottery", {
              ns: "ui",
              defaultValue: "Start lottery",
            })}
          </button>
        ) : null}
        {type === "readyToRun" &&
        (state.revealState === "running" || state.revealState === "paused") ? (
          <PlayPauseNext
            onPlay={handleResume}
            onPause={handlePause}
            onNext={handleShowOne}
            paused={state.revealState === "paused"}
            titlePlay={i18n.t("draftLottery.resumeLottery", {
              ns: "ui",
              defaultValue: "Resume lottery",
            })}
            titlePause={i18n.t("draftLottery.pauseLottery", {
              ns: "ui",
              defaultValue: "Pause lottery",
            })}
            titleNext={i18n.t("draftLottery.showNextPick", {
              ns: "ui",
              defaultValue: "Show next pick",
            })}
          />
        ) : null}
      </div>
      {tooSlow ? (
        <div className="text-warning mb-3">
          {i18n.t("draftLottery.tooSlowWarning", {
            ns: "ui",
            defaultValue:
              "Warning: Computing exact odds for so many teams and picks is slow, so estimates are shown below. When the actual lottery occurs it is simulated with complete accuracy.",
          })}
        </div>
      ) : null}

      <MobileExpertDisclosure
        className="mt-3"
        data-draft-lottery-probability-depth=""
        id="draft-lottery-detail"
        expertHostId="draft-lottery-probabilities"
        summary={
          isHistoricalLottery
            ? i18n.t("draftLottery.p2s06ReadonlyOrderDetail", {
                ns: "ui",
                defaultValue: "Final order and probability record",
              })
            : i18n.t("draftLottery.p2v2ProbabilityDetail", {
                ns: "ui",
                defaultValue: "Lottery odds and probabilities",
              })
        }
      >
        {table}
        {desktopLotteryTable}
      </MobileExpertDisclosure>

      {!showAll && draftPicks?.some((dp) => dp.round > 1) ? (
        <button
          className="btn btn-secondary mt-3"
          data-draft-lottery-show-all-rounds=""
          onClick={() => {
            setShowAll(true);
          }}
          type="button"
        >
          {i18n.t("draftLottery.showAllRounds", {
            ns: "ui",
            defaultValue: "Show all rounds",
          })}
        </button>
      ) : null}
    </>
  );
};

const ColaTable = ({
  colaTable,
  userTid,
}: {
  colaTable: NonNullable<Props["colaTable"]>;
  userTid: Props["userTid"];
}) => {
  const cols = getCols(["Team", "Chances"]);

  const rows = colaTable.map((t) => {
    return {
      key: t.tid,
      data: [
        wrappedTeamLogoAndName(
          {
            tid: t.tid,
            seasonAttrs: t,
          },
          createMobileCrossTabHref(
            helpers.leagueUrl(["roster", `${t.abbrev}_${t.tid}`]),
          ),
          {
            noAbbrev: true,
          },
        ),
        t.cola ?? 0,
      ],
    };
  });

  return (
    <div className="mt-5">
      <h2 className="mb-2">
        {i18n.t("draftLottery.colaLotteryChances", {
          ns: "ui",
          defaultValue: "COLA lottery chances",
        })}
      </h2>
      {(() => {
        const colaDataTable = (
          <DataTable
            className="mb-0"
            cols={cols}
            defaultSort={[1, "desc"]}
            hideAllControls
            nonfluid
            name="DraftLotteryCola"
            rows={rows}
            pagination={false}
          />
        );

        // B3: on mobile the full chances list folds behind a counted
        // expert-depth entry; desktop keeps the always-visible table.
        return window.mobile ? (
          <MobileExpertDisclosure
            count={i18n.t("draftLottery.colaTableCount", {
              ns: "ui",
              defaultValue: "{{count}} teams",
              count: rows.length,
            })}
            data-b3-cola-table-depth=""
            expertHostId="draft-lottery-cola-full-table"
            summary={i18n.t("draftLottery.colaFullTable", {
              ns: "ui",
              defaultValue: "Full chances table",
            })}
          >
            {colaDataTable}
          </MobileExpertDisclosure>
        ) : (
          colaDataTable
        );
      })()}
    </div>
  );
};

const DraftLottery = (props: Props) => {

  useTitleBar({
    title: NO_LOTTERY_DRAFT_TYPES.has(props.draftType as any)
      ? i18n.t("draftLottery.draftOrderTitle", {
          ns: "ui",
          defaultValue: "Draft Order",
        })
      : i18n.t("menu.Draft Lottery", {
          ns: "ui",
          defaultValue: "Draft Lottery",
        }),
    jumpTo: true,
    jumpToSeason: props.season,
    dropdownView: "draft_lottery",
    dropdownFields: {
      seasons: props.season,
    },
  });

  const { colaTable, userTid } = props;

  return (
    // G2d-1b: v5Content root. The core-task / archive-readonly template
    // wrapper (eyebrow/subtitle/title self-narration + the two template
    // markers) is gone; the page anchors it carried are remounted on this
    // root div, and the page title lives on the TitleBar only.
    <div
      data-draft-lottery-production-root=""
      data-draft-lottery-result-count={props.result?.length ?? 0}
      data-draft-lottery-second-round-count={
        props.draftPicks?.filter((dp) => dp.round === 2).length ?? 0
      }
    >
      {/* G2d-2: the hub tab strip retired; a single breadcrumb back to the
          converged draft entry replaces it. */}
      <a
        className="btn btn-link btn-sm px-0 mb-2 d-inline-flex align-items-center gap-1"
        data-draft-return-link=""
        href={helpers.leagueUrl(["draft"])}
      >
        <span aria-hidden="true">←</span>
        {i18n.t("draftHub.returnToDraft", { ns: "ui", defaultValue: "Back to the draft" })}
      </a>
      <MobileArchiveSeasonNav
        currentLabel={i18n.t("draftLottery.p2s03SeasonNavLabel", {
          ns: "ui",
          defaultValue: "Lottery season",
        })}
        currentValue={props.season}
        nextHref={helpers.leagueUrl(["draft_lottery", props.season + 1])}
        nextLabel={i18n.t("draftLottery.p2dViewCompleteOrder", {
          ns: "ui",
          defaultValue: "View complete order",
        })}
        previousHref={helpers.leagueUrl(["draft_lottery", props.season - 1])}
        previousLabel={i18n.t("draftLottery.p2dViewPreviousStage", {
          ns: "ui",
          defaultValue: "View previous stage",
        })}
        rootHref={helpers.leagueUrl(["draft_lottery"])}
        rootLabel={i18n.t("draftLottery.p2dCurrentLottery", {
          ns: "ui",
          defaultValue: "Current lottery",
        })}
      />

      {props.type === "projected" && props.draftType !== "noLottery" ? (
        <p>
          {i18n.t("draftLottery.projectedMessage", {
            ns: "ui",
            defaultValue:
              "This is what the draft lottery probabilities would be if the lottery was held right now.",
          })}
        </p>
      ) : null}

      {props.showExpansionTeamMessage ? (
        <p>
          {i18n.t("draftLottery.expansionTeamMessage", {
            ns: "ui",
            defaultValue:
              "New expansion teams are treated as if they won half their games but missed the playoffs.",
          })}
        </p>
      ) : null}

      <DraftLotteryTable {...props} />

      {colaTable ? <ColaTable colaTable={colaTable} userTid={userTid} /> : null}
    </div>
  );
};

export default DraftLottery;
